import { Coins } from "lucide-react";
import { useResourceCredits, formatCredits, type CreditBreakdown } from "@/hooks/useResourceCredits";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

interface ResourceCreditDisplayProps {
  resourceType: "world" | "adventure";
  resourceId: string | null;
  className?: string;
}

function BreakdownRow({ label, amount }: { label: string; amount: number }) {
  if (amount === 0) return null;
  return (
    <div className="flex justify-between gap-4">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono">{formatCredits(amount)}</span>
    </div>
  );
}

export function ResourceCreditDisplay({ resourceType, resourceId, className }: ResourceCreditDisplayProps) {
  const { credits, loading } = useResourceCredits(resourceType, resourceId);

  if (loading || credits.total === 0) return null;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            "inline-flex items-center gap-1.5 text-xs font-mono px-2.5 py-1 rounded-md bg-muted/50 text-muted-foreground hover:bg-muted transition-colors cursor-default",
            className
          )}
        >
          <Coins className="w-3.5 h-3.5 text-amber-500" />
          {formatCredits(credits.total)} credits used
        </span>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="text-xs space-y-1 min-w-[160px]">
        <div className="font-semibold mb-1">Credit Breakdown</div>
        <BreakdownRow label="AI Text" amount={credits.text} />
        <BreakdownRow label="Voice" amount={credits.voice} />
        <BreakdownRow label="Images" amount={credits.image} />
        <BreakdownRow label="Web Search" amount={credits.serp} />
        <BreakdownRow label="Proxy" amount={credits.proxy} />
        <div className="border-t border-border pt-1 mt-1 flex justify-between gap-4 font-semibold">
          <span>Total</span>
          <span className="font-mono">{formatCredits(credits.total)}</span>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
